import { Boxes, Flag, House, LayoutGrid, type LucideIcon, MousePointer2, Pentagon, Spline } from "lucide-react";
import { useApp, type Tool } from "../state/store.ts";
import { controller } from "../app/controller.ts";

const TOOLS: { id: Tool; icon: LucideIcon; label: string; key: string }[] = [
  { id: "select", icon: MousePointer2, label: "Select / move", key: "1" },
  { id: "path", icon: Spline, label: "Path", key: "2" },
  { id: "area", icon: Pentagon, label: "Area", key: "3" },
  { id: "building", icon: House, label: "Building", key: "4" },
  { id: "marker", icon: Flag, label: "Marker", key: "5" },
];

/**
 * The floating tool dock on the left of the map.
 * @param tools  which drawing tools to show (all by default)
 * @param panels show the Layouts (6) / Schematics (7) buttons
 */
export function Toolbar({ tools, panels = ["layouts", "schematics"] }: { tools?: Tool[]; panels?: ("layouts" | "schematics")[] } = {}) {
  const tool = useApp((s) => s.tool);
  const set = useApp((s) => s.set);
  const layoutPanel = useApp((s) => s.layoutPanel);
  const layoutsOn = !!layoutPanel && layoutPanel !== "schematics";
  return (
    <nav className="toolbar dock glass" aria-label="Tools">
      {TOOLS.filter((t) => !tools || tools.includes(t.id)).map((t) => (
        <button key={t.id} className="tool" aria-pressed={tool === t.id} onClick={() => set({ tool: t.id })} title={`${t.label} · ${t.key}`} aria-label={`${t.label} (${t.key})`} data-tour={`tool-${t.id}`}>
          <t.icon size={18} aria-hidden="true" />
          <span className="kbd-badge" aria-hidden="true">{t.key}</span>
        </button>
      ))}
      {panels.length > 0 && <span className="toolbar-sep" aria-hidden="true" />}
      {panels.includes("layouts") && (
        <button className="tool" aria-pressed={layoutsOn} onClick={() => controller.openLayouts(layoutsOn ? null : "fill")} title="Layouts & templates · 6" aria-label="Layouts and templates (6)" data-tour="tool-layouts">
          <LayoutGrid size={18} aria-hidden="true" />
          <span className="kbd-badge" aria-hidden="true">6</span>
        </button>
      )}
      {panels.includes("schematics") && (
        <button className="tool" aria-pressed={layoutPanel === "schematics"} onClick={() => controller.openLayouts(layoutPanel === "schematics" ? null : "schematics")} title="Schematics: import builds from .litematic / .schem / .schematic / .nbt files · 7" aria-label="Schematics (7)" data-tour="tool-schematics">
          <Boxes size={18} aria-hidden="true" />
          <span className="kbd-badge" aria-hidden="true">7</span>
        </button>
      )}
    </nav>
  );
}
